import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  UserCircle, Lock, Camera, Sliders, SignOut, Check, CaretDown, Briefcase,
} from '@phosphor-icons/react';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * Profile avatar dropdown — strictly personal user actions only.
 * (Workspace/Settings live in the side drawer per the spec.)
 */
export default function ProfileMenu() {
  const { user, logout, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const initial = (user?.name || 'U').charAt(0).toUpperCase();
  const photo = user?.photo_url || '';

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try {
      await api.post('/users/me/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo updated');
      await refresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        data-testid="profile-avatar-btn"
        className="flex items-center gap-1.5 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 px-1 py-0.5 transition">
        {photo ? (
          <img src={photo} alt={user?.name} className="h-8 w-8 rounded-full object-cover ring-2 ring-stone-200 dark:ring-stone-700" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#6B4D5F] to-[#3D2B33] text-white flex items-center justify-center text-xs font-bold ring-2 ring-stone-200 dark:ring-stone-700">
            {initial}
          </div>
        )}
        <CaretDown size={10} className="text-stone-400 hidden sm:block" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-xl bg-white dark:bg-stone-900 shadow-2xl ring-1 ring-stone-200 dark:ring-stone-800 overflow-hidden z-50"
          data-testid="profile-menu">
          <div className="px-4 py-4 border-b border-stone-100 dark:border-stone-800 flex items-center gap-3">
            {photo ? (
              <img src={photo} alt={user?.name} className="h-12 w-12 rounded-full object-cover" />
            ) : (
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#6B4D5F] to-[#3D2B33] text-white flex items-center justify-center text-base font-bold">
                {initial}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-stone-800 dark:text-stone-100 truncate">{user?.name}</p>
              <p className="text-[10px] uppercase tracking-wider text-stone-500">{user?.role}</p>
            </div>
          </div>

          <div className="py-1">
            <Item icon={UserCircle} label="My Profile" onClick={() => { setOpen(false); navigate('/profile'); }} testid="pm-profile" />
            {user?.is_contractor && (
              <Item icon={Briefcase} label="Contractor Dashboard"
                onClick={() => { setOpen(false); navigate('/my-contractor'); }}
                testid="pm-contractor-portal" />
            )}
            <Item icon={Lock} label="Change Password" onClick={() => { setOpen(false); navigate('/profile?tab=security'); }} testid="pm-password" />
            <label className="flex items-center gap-3 w-full px-4 py-2 hover:bg-stone-50 dark:hover:bg-stone-800/50 cursor-pointer text-sm text-stone-700 dark:text-stone-200">
              <Camera size={16} weight="duotone" className="text-stone-500" />
              <span>{photo ? 'Change Photo' : 'Upload Photo'}</span>
              <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="pm-photo-input" />
            </label>
            <Item icon={Sliders} label="Preferences" onClick={() => { setOpen(false); navigate('/profile?tab=preferences'); }} testid="pm-prefs" />
          </div>
          <div className="border-t border-stone-100 dark:border-stone-800 py-1">
            <Item icon={SignOut} label="Sign out" danger onClick={() => { setOpen(false); logout(); }} testid="pm-logout" />
          </div>
        </div>
      )}
    </div>
  );
}

function Item({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-3 w-full px-4 py-2 hover:bg-stone-50 dark:hover:bg-stone-800/50 text-sm transition
        ${danger ? 'text-red-600 hover:text-red-700' : 'text-stone-700 dark:text-stone-200'}`}>
      <Icon size={16} weight="duotone" className={danger ? 'text-red-500' : 'text-stone-500'} />
      <span>{label}</span>
    </button>
  );
}
